import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, query } from '../api/client';
import type { License } from '../api/types';
import { IconLicense } from '../components/icons';
import { Runway, StatusPill } from '../components/license';
import { Button, EmptyState, ErrorNote, SkeletonRows } from '../components/ui';
import { formatDate } from '../lib/license';

type Filter = 'all' | 'expired' | 'expiring' | 'usable' | 'suspended';

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'expired', label: 'Expired' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'usable', label: 'Working' },
  { value: 'suspended', label: 'Suspended' },
];

/**
 * Every license across every company, sorted by expiry — soonest first. That
 * ordering is the point: the top of this table is always the next thing to
 * deal with.
 */
export function LicensesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const focusId = params.get('focus');
  const [filter, setFilter] = useState<Filter>((params.get('state') as Filter) ?? 'all');
  const focusRef = useRef<HTMLTableRowElement>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['licenses', filter],
    queryFn: () =>
      api.get<{ licenses: License[] }>(
        `/api/staff/licenses${query({ state: filter, withinDays: 30 })}`,
      ),
  });

  const licenses = data?.licenses ?? [];

  // Arriving from the dashboard's attention list should land on the row, not
  // near it.
  useEffect(() => {
    if (focusId && focusRef.current) {
      focusRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [focusId, licenses.length]);

  const changeFilter = (next: Filter) => {
    setFilter(next);
    const updated = new URLSearchParams(params);
    if (next === 'all') updated.delete('state');
    else updated.set('state', next);
    updated.delete('focus');
    setParams(updated, { replace: true });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Licenses</h1>
          <p className="page-head__sub">
            Soonest expiry first. Expiry is computed live from the clock, so what you see here is
            exactly what the sign-in check enforces.
          </p>
        </div>
      </div>

      <section className="panel">
        <div className="panel__head">
          <div className="segmented" role="group" aria-label="Filter licenses by state">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={filter === option.value}
                onClick={() => changeFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {!isPending && (
            <span className="small faint tabular">
              {licenses.length} license{licenses.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {isPending && <SkeletonRows rows={6} />}
        {isError && (
          <div className="panel__body">
            <ErrorNote error={error} />
          </div>
        )}

        {!isPending && !isError && licenses.length === 0 && (
          <EmptyState
            icon={<IconLicense size={30} />}
            title={filter === 'all' ? 'No licenses recorded' : `Nothing is ${labelFor(filter)}`}
            body={
              filter === 'all'
                ? 'Open a company and record what they bought. A license is what lets their people sign in.'
                : 'Switch the filter to see the rest of the estate.'
            }
            action={
              filter === 'all' ? (
                <Button variant="primary" onClick={() => navigate('/companies')}>
                  Go to companies
                </Button>
              ) : (
                <Button onClick={() => changeFilter('all')}>Show all</Button>
              )
            }
          />
        )}

        {licenses.length > 0 && (
          <div className="table-wrap scroll-area">
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Product</th>
                  <th>State</th>
                  <th style={{ minWidth: 180 }}>Runway</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr
                    key={license.id}
                    ref={license.id === focusId ? focusRef : undefined}
                    data-clickable="true"
                    style={
                      license.id === focusId
                        ? { background: 'var(--accent-wash)' }
                        : undefined
                    }
                    onClick={() => navigate(`/companies/${license.company_id}`)}
                  >
                    <td>
                      <div className="cell-stack">
                        <span className="cell-strong">{license.company_name}</span>
                        <span className="cell-meta mono">{license.company_code}</span>
                      </div>
                    </td>
                    <td>{license.product_name}</td>
                    <td>
                      <StatusPill license={license} />
                    </td>
                    <td>
                      <Runway license={license} />
                    </td>
                    <td className="cell-meta tabular">{formatDate(license.starts_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function labelFor(filter: Filter): string {
  switch (filter) {
    case 'expired':
      return 'expired';
    case 'expiring':
      return 'expiring within 30 days';
    case 'usable':
      return 'working';
    case 'suspended':
      return 'suspended';
    default:
      return 'recorded';
  }
}
